<template>
  <div class="regional-container">
    <!-- No Results Message -->
    <div v-if="cards.length === 0" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-map-marked-alt"></i>
      </div>
      <p class="empty-text">{{ $t('noRegionalsTitle') }}</p>
      <p class="empty-sub">{{ $t('noRegionalsDescription') }}</p>
    </div>

    <!-- Cards Grid -->
    <div class="cards-grid">
      <div
        v-for="(card, index) in cards"
        :key="index"
        class="regional-card"
      >
        <!-- Card Header with accent bar -->
        <div class="card-header-accent"></div>

        <div class="card-body">
          <!-- Region name -->
          <div class="region-name-row">
            <div class="region-icon-circle">
              <i class="fas fa-building"></i>
            </div>
            <h3 class="region-name">{{ card.name || $t('noDataAvailable') }}</h3>
          </div>

          <div class="card-divider"></div>

          <!-- Contact details -->
          <ul class="contact-list">
            <!-- Phone -->
            <li class="contact-row">
              <div class="contact-icon phone-icon">
                <i class="fas fa-phone-alt"></i>
              </div>
              <a
                v-if="isValid(card.regional_contact)"
                :href="`tel:${card.regional_contact}`"
                class="contact-value contact-link"
                :title="card.regional_contact"
              >
                {{ card.regional_contact }}
              </a>
              <span v-else class="contact-value contact-empty">
                {{ $t('noPhoneNumber') }}
              </span>
            </li>

            <!-- Email — clickable mailto -->
            <li class="contact-row">
              <div class="contact-icon email-icon">
                <i class="fas fa-envelope"></i>
              </div>
              <a
                v-if="isValid(card.email)"
                :href="`mailto:${card.email}`"
                class="contact-value contact-link"
                :title="card.email"
              >
                {{ card.email }}
              </a>
              <span v-else class="contact-value contact-empty">
                {{ $t('noEmailAddress') }}
              </span>
            </li>

            <!-- Location — clickable, opens Google Maps with live directions -->
            <li class="contact-row">
              <div class="contact-icon location-icon">
                <i class="fas fa-map-marker-alt"></i>
              </div>
              <button
                v-if="isValid(card.location)"
                class="contact-value contact-link location-btn"
                :title="$t('getDirections') + ': ' + card.location"
                @click="openDirections(card)"
              >
                <span class="location-text">{{ card.location }}</span>
                <i class="fas fa-external-link-alt location-ext-icon"></i>
              </button>
              <span v-else class="contact-value contact-empty">
                {{ $t('noLocation') }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "RegionalRepresentative",
  props: {
    cards: {
      type: Array,
      required: true,
    },
  },
  methods: {
    isValid(val) {
      if (val === null || val === undefined) return false;
      const s = String(val).toLowerCase().trim();
      return s !== '' && s !== 'nan' && s !== 'none';
    },

    /**
     * Opens Google Maps with live directions from the user's current location
     * to the regional delegation. Falls back to a simple search if geolocation
     * is denied or unavailable.
     */
    openDirections(card) {
      // Use only the location address for Google Maps search, not the regional name
      const destination = (card.location && this.isValid(card.location))
        ? card.location
        : '';

      if (!destination.trim()) return;

      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const url = `https://www.google.com/maps/dir/?api=1&origin=${latitude},${longitude}&destination=${encodeURIComponent(destination)}&travelmode=driving`;
            window.open(url, '_blank', 'noopener,noreferrer');
          },
          () => {
            // User denied geolocation — fall back to search
            const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destination)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      } else {
        const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destination)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
  },
};
</script>

<style scoped>
.regional-container {
  padding: 0.5rem;
  max-width: 100%;
  box-sizing: border-box;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem 1.5rem;
  background: #f0f7ff;
  border-radius: 1rem;
  border: 1px solid #dbeafe;
}
.empty-icon {
  font-size: 2.5rem;
  color: #93c5fd;
  margin-bottom: 1rem;
}
.empty-text {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e3a5f;
  margin-bottom: 0.5rem;
}
.empty-sub {
  font-size: 0.9rem;
  color: #6b7280;
}

/* Cards grid */
.cards-grid {
  display: grid;
  gap: 1.25rem;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
}

/* Individual card */
.regional-card {
  background: #ffffff;
  border-radius: 0.875rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06), 0 4px 12px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
}
.regional-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 16px rgba(20, 74, 120, 0.12), 0 2px 6px rgba(0, 0, 0, 0.06);
}

/* Accent bar at top of card */
.card-header-accent {
  height: 4px;
  background: linear-gradient(90deg, #144A78, #2563eb, #3b82f6);
}

.card-body {
  padding: 1.25rem 1.5rem 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Region name */
.region-name-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}
.region-icon-circle {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #144A78;
  font-size: 0.9rem;
  flex-shrink: 0;
}
.region-name {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.3;
}

.card-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0.25rem 0 0.875rem;
}

/* Contact list */
.contact-list {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.contact-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-height: 2.25rem;
}

/* Icon circles with different accent colors */
.contact-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.8rem;
}
.phone-icon  { background: #ecfdf5; color: #059669; }
.email-icon  { background: #fef3c7; color: #d97706; }
.location-icon { background: #fee2e2; color: #dc2626; }

/* Contact value styling */
.contact-value {
  flex: 1;
  min-width: 0;
  font-size: 0.875rem;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Clickable links */
.contact-link {
  color: #144A78;
  text-decoration: none;
  transition: color 0.2s;
  cursor: pointer;
}
.contact-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

/* Location button styling */
.location-btn {
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}
.location-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.location-ext-icon {
  font-size: 0.65rem;
  opacity: 0;
  transition: opacity 0.2s;
  flex-shrink: 0;
}
.location-btn:hover .location-ext-icon {
  opacity: 0.7;
}

/* Empty value styling */
.contact-empty {
  color: #9ca3af;
  font-style: italic;
}

/* Responsive */
@media (max-width: 640px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 640px) and (max-width: 1024px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) and (max-width: 1280px) {
  .cards-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1280px) {
  .cards-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>